import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  FileEdit, Save, RotateCcw, Rocket, ExternalLink, RefreshCw,
  Sparkles, Info, LayoutList, Users2, Coffee, Layers, Menu as MenuIcon, MessageSquare,
  Loader2, Eye, ChevronRight, Megaphone,
} from 'lucide-react';
import { api } from '@/lib/api';
import ImagePicker from '@/components/admin/ImagePicker';
import RepeaterList from '@/components/admin/RepeaterList';
import ConfirmDialog from '@/components/admin/ConfirmDialog';
import { DEFAULT_SITE_CONTENT, mergeContent } from '@/constants/defaultSiteContent';

const SECTIONS = [
  { key: 'hero', label: 'Hero', icon: Sparkles, help: 'Top-of-page banner: badge, headline, subhead, body, CTA, showcase image and neon overlay.' },
  { key: 'marquee', label: 'Marquee', icon: Megaphone, help: 'Rolling announcements ribbon under the hero — perfect for promos, tournaments, or brand slogans.' },
  { key: 'about', label: 'About', icon: Info, help: 'The Project section: story copy, iconic stats, and framed inset image.' },
  { key: 'services', label: 'Services', icon: LayoutList, help: 'The 4 service cards — title, description and photo for each.' },
  { key: 'community', label: 'Community', icon: Users2, help: 'Immersive banner with pillars and quote-style headline.' },
  { key: 'footer', label: 'Footer', icon: Coffee, help: 'Location, socials, copyright, CTA.' },
  { key: 'navbar', label: 'Navigation', icon: MenuIcon, help: 'Top nav labels, order and location pill.' },
];

const PREVIEW_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api$/, '') || '';

export default function AdminContentStudio() {
  const [draft, setDraft] = useState(DEFAULT_SITE_CONTENT);
  const [published, setPublished] = useState(DEFAULT_SITE_CONTENT);
  const [meta, setMeta] = useState({ updated_at: null, published_at: null });
  const [activeSection, setActiveSection] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [previewNonce, setPreviewNonce] = useState(0);
  const iframeRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/site-content');
      const d = mergeContent(DEFAULT_SITE_CONTENT, data?.draft || {});
      const p = mergeContent(DEFAULT_SITE_CONTENT, data?.published || {});
      setDraft(d);
      setPublished(p);
      setMeta({ updated_at: data?.updated_at, published_at: data?.published_at });
      setDirty(false);
    } catch (err) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchSection = (key, next) => {
    setDraft((prev) => ({ ...prev, [key]: next }));
    setDirty(true);
  };

  const saveDraft = async ({ silent = false } = {}) => {
    setSaving(true);
    try {
      await api.put('/admin/site-content', draft);
      setDirty(false);
      setMeta((m) => ({ ...m, updated_at: new Date().toISOString() }));
      setPreviewNonce((n) => n + 1);
      if (!silent) toast.success('Draft saved. Preview updated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const publishNow = async () => {
    setPublishing(true);
    try {
      if (dirty) await api.put('/admin/site-content', draft);
      await api.post('/admin/site-content/publish');
      setPublished(draft);
      setMeta({ updated_at: new Date().toISOString(), published_at: new Date().toISOString() });
      setDirty(false);
      setPreviewNonce((n) => n + 1);
      toast.success('Content published. Live site is updated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const revertDraft = async () => {
    try {
      const { data } = await api.post('/admin/site-content/revert-draft');
      const d = mergeContent(DEFAULT_SITE_CONTENT, data?.draft || {});
      setDraft(d);
      setDirty(false);
      setPreviewNonce((n) => n + 1);
      toast.success('Draft reverted to the last published version.');
    } catch (err) {
      toast.error('Revert failed');
    }
  };

  const resetDefaults = async () => {
    try {
      const { data } = await api.post('/admin/site-content/reset-defaults');
      const d = mergeContent(DEFAULT_SITE_CONTENT, data?.draft || {});
      setDraft(d);
      setDirty(false);
      setPreviewNonce((n) => n + 1);
      toast.success('Draft reset to factory defaults.');
    } catch (err) {
      toast.error('Reset failed');
    }
  };

  const statusPill = useMemo(() => {
    if (dirty) return { label: 'Unsaved changes', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
    if (meta.updated_at && meta.published_at && new Date(meta.updated_at) > new Date(meta.published_at)) {
      return { label: 'Draft ahead of published', color: 'bg-sky-500/15 text-sky-400 border-sky-500/30' };
    }
    return { label: 'All changes published', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' };
  }, [dirty, meta]);

  const currentEditor = () => {
    const c = draft[activeSection] || {};
    const set = (next) => patchSection(activeSection, next);
    switch (activeSection) {
      case 'hero': return <HeroEditor content={c} onChange={set} />;
      case 'marquee': return <MarqueeEditor content={c} onChange={set} />;
      case 'about': return <AboutEditor content={c} onChange={set} />;
      case 'services': return <ServicesEditor content={c} onChange={set} />;
      case 'community': return <CommunityEditor content={c} onChange={set} />;
      case 'footer': return <FooterEditor content={c} onChange={set} />;
      case 'navbar': return <NavbarEditor content={c} onChange={set} />;
      default: return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2">
            <FileEdit className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Content Studio
          </h1>
          <p className="mt-1 text-sm text-white/60 max-w-xl">Design, copywriting and imagery for the entire public homepage — with a live preview and safe Draft → Publish flow.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${statusPill.color}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current" /> {statusPill.label}
          </span>
          <button data-testid="content-open-live" onClick={() => window.open('/', '_blank')} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/15 text-white/80 hover:bg-white/5">
            <ExternalLink className="w-3.5 h-3.5" /> Live site
          </button>
          <button data-testid="content-revert-btn" onClick={() => setConfirm('revert')} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/15 text-white/80 hover:bg-white/5">
            <RotateCcw className="w-3.5 h-3.5" /> Revert draft
          </button>
          <button data-testid="content-save-btn" onClick={() => saveDraft()} disabled={saving || !dirty} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold disabled:opacity-40">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save draft
          </button>
          <button data-testid="content-publish-btn" onClick={() => setConfirm('publish')} disabled={publishing} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] text-[var(--pg-admin-accent-fg)] font-semibold disabled:opacity-60">
            {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />} Publish
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 text-white/60 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading content…</div>
      ) : (
        <div className="mt-6 grid xl:grid-cols-[220px_minmax(0,1fr)_minmax(0,1.1fr)] gap-5">
          {/* Sections rail */}
          <aside className="xl:sticky xl:top-6 self-start">
            <div className="flex xl:flex-col gap-1.5 overflow-x-auto xl:overflow-visible -mx-4 sm:mx-0 px-4 sm:px-0 pb-2 xl:pb-0">
              {SECTIONS.map((s) => (
                <button
                  key={s.key}
                  data-testid={`content-section-${s.key}`}
                  onClick={() => setActiveSection(s.key)}
                  className={`shrink-0 xl:shrink w-auto xl:w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-colors ${activeSection === s.key ? 'bg-[var(--pg-admin-accent)] text-[var(--pg-admin-accent-fg)] font-semibold border-transparent' : 'border-white/10 bg-[#141414] text-white/80 hover:bg-white/5'}`}
                >
                  <s.icon className="w-4 h-4" /> <span className="whitespace-nowrap">{s.label}</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-60 hidden xl:inline-block" />
                </button>
              ))}
            </div>
            <div className="hidden xl:block mt-3 rounded-xl border border-white/10 bg-[#141414] p-3 text-[11px] text-white/60 leading-relaxed">
              {SECTIONS.find((s) => s.key === activeSection)?.help}
            </div>
          </aside>

          {/* Editor */}
          <section className="rounded-2xl border border-white/10 bg-[#141414] p-5 min-w-0">
            {currentEditor()}
          </section>

          {/* Live preview */}
          <aside className="rounded-2xl border border-white/10 bg-[#141414] p-3 min-h-[560px] flex flex-col">
            <div className="flex items-center justify-between px-1 pb-2">
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/60 font-semibold"><Eye className="w-3.5 h-3.5" /> Live preview (draft)</div>
              <button onClick={() => setPreviewNonce((n) => n + 1)} className="text-[11px] text-white/60 hover:text-white inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
            </div>
            <div className="flex-1 rounded-lg overflow-hidden border border-white/10 bg-black/20">
              <iframe
                ref={iframeRef}
                key={previewNonce}
                title="Public preview"
                src={`${PREVIEW_BASE}/?preview=draft&v=${previewNonce}#${activeSection}`}
                className="w-full h-full min-h-[540px]"
              />
            </div>
            <div className="mt-2 text-[10px] text-white/40 px-1">Tip: click Save draft to refresh the preview with your latest edits.</div>
          </aside>
        </div>
      )}

      <ConfirmDialog
        open={confirm === 'publish'}
        onOpenChange={(v) => !v && setConfirm(null)}
        title="Publish to the live site?"
        description="Everyone visiting your site will immediately see this version. You can always revert to a previous version by editing again."
        confirmText="Publish now"
        onConfirm={async () => { setConfirm(null); await publishNow(); }}
      />
      <ConfirmDialog
        open={confirm === 'revert'}
        onOpenChange={(v) => !v && setConfirm(null)}
        title="Revert draft to the last published version?"
        description="Your unsaved edits will be discarded and the draft will match the current live site."
        confirmText="Revert draft"
        destructive
        onConfirm={async () => { setConfirm(null); await revertDraft(); }}
      />
      <ConfirmDialog
        open={confirm === 'reset'}
        onOpenChange={(v) => !v && setConfirm(null)}
        title="Reset the draft to factory defaults?"
        description="The original Padel Gems content will replace your draft. The live version is not affected until you publish."
        confirmText="Reset draft"
        destructive
        onConfirm={async () => { setConfirm(null); await resetDefaults(); }}
      />

      <div className="mt-4 text-right">
        <button onClick={() => setConfirm('reset')} className="text-[11px] text-white/40 hover:text-white/70 underline underline-offset-2">Reset draft to factory defaults</button>
      </div>
    </div>
  );
}

// ============ Field primitives ============
const Field = ({ label, hint, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-[10px] text-white/40 mt-1">{hint}</span>}
  </label>
);

const inputCls = 'w-full border border-white/10 bg-black/30 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[var(--pg-admin-accent)] focus:ring-2 focus:ring-[var(--pg-admin-accent-ring)]';
const TextInput = (props) => <input {...props} className={`${inputCls} ${props.className || ''}`} />;
const TextArea = (props) => <textarea {...props} className={`${inputCls} resize-none ${props.className || ''}`} />;

const SectionTitle = ({ children, hint }) => (
  <div className="mb-4">
    <h2 className="font-display text-2xl text-white">{children}</h2>
    {hint && <p className="text-xs text-white/50 mt-1">{hint}</p>}
  </div>
);

// ============ Section editors ============

function HeroEditor({ content, onChange }) {
  const c = content || {};
  const patch = (field, value) => onChange({ ...c, [field]: value });
  return (
    <>
      <SectionTitle hint="Change your headline, subheading, CTA and both showcase images.">Hero</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Badge (small pill)">
          <TextInput value={c.badge || ''} onChange={(e) => patch('badge', e.target.value)} />
        </Field>
        <Field label="CTA label">
          <TextInput value={c.cta_label || ''} onChange={(e) => patch('cta_label', e.target.value)} />
        </Field>
        <Field label="Headline (top line)">
          <TextInput value={c.headline_top || ''} onChange={(e) => patch('headline_top', e.target.value)} />
        </Field>
        <Field label="Headline (bottom line)">
          <TextInput value={c.headline_bottom || ''} onChange={(e) => patch('headline_bottom', e.target.value)} />
        </Field>
        <Field label="Eyebrow line 1">
          <TextInput value={c.eyebrow_line1 || ''} onChange={(e) => patch('eyebrow_line1', e.target.value)} />
        </Field>
        <Field label="Eyebrow line 2">
          <TextInput value={c.eyebrow_line2 || ''} onChange={(e) => patch('eyebrow_line2', e.target.value)} />
        </Field>
      </div>
      <Field label="Body copy" className="mt-4">
        <TextArea rows={4} value={c.body || ''} onChange={(e) => patch('body', e.target.value)} />
      </Field>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <ImagePicker
          label="Showcase image (right frame)"
          section="hero"
          aspect="4/5"
          value={c.image_url}
          onChange={(url) => patch('image_url', url)}
        />
        <ImagePicker
          label="Background image (atmospheric)"
          section="hero"
          aspect="16/9"
          value={c.bg_image_url}
          onChange={(url) => patch('bg_image_url', url)}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Field label="Image alt (accessibility)">
          <TextInput value={c.image_alt || ''} onChange={(e) => patch('image_alt', e.target.value)} />
        </Field>
        <Field label="Script overlay (image bottom-right)">
          <div className="grid grid-cols-2 gap-2">
            <TextInput placeholder="Cursive" value={c.script_text || ''} onChange={(e) => patch('script_text', e.target.value)} />
            <TextInput placeholder="Small caption" value={c.script_sub || ''} onChange={(e) => patch('script_sub', e.target.value)} />
          </div>
        </Field>
      </div>

      <Field label="Neon sign (one word per line)" className="mt-4" hint="Leave empty to hide the neon sign overlay.">
        <TextArea rows={4} value={(c.neon_lines || []).join('\n')} onChange={(e) => patch('neon_lines', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />
      </Field>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-2">Pills (below body)</div>
        <RepeaterList
          items={c.pills || []}
          onChange={(v) => patch('pills', v)}
          addLabel="Add pill"
          newItem={() => ({ icon: 'Sparkles', label: 'NEW' })}
          max={5}
          renderItem={(it, i, up) => (
            <div className="grid grid-cols-[1fr_1fr] gap-3">
              <Field label="Icon (lucide name)">
                <TextInput value={it.icon || ''} onChange={(e) => up('icon', e.target.value)} placeholder="Users" />
              </Field>
              <Field label="Label">
                <TextInput value={it.label || ''} onChange={(e) => up('label', e.target.value)} />
              </Field>
            </div>
          )}
        />
      </div>
    </>
  );
}

function MarqueeEditor({ content, onChange }) {
  const c = content || {};
  const patch = (field, value) => onChange({ ...c, [field]: value });
  const enabled = c.enabled !== false;
  return (
    <>
      <SectionTitle hint="A rolling announcements ribbon under the Hero — great for promos, tournaments, and brand slogans.">Marquee</SectionTitle>

      {/* Master toggle */}
      <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/20 px-4 py-3 mb-4">
        <div>
          <div className="text-sm text-white font-semibold">Show marquee on the homepage</div>
          <div className="text-[11px] text-white/50">Toggle off to instantly hide the ribbon without losing your items.</div>
        </div>
        <button
          type="button"
          onClick={() => patch('enabled', !enabled)}
          data-testid="marquee-enabled-toggle"
          role="switch"
          aria-checked={enabled}
          className={`relative w-12 h-7 rounded-full transition-colors ${enabled ? 'bg-[var(--pg-admin-accent)]' : 'bg-white/15'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} />
        </button>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <Field label="Theme">
          <select value={c.theme || 'green'} onChange={(e) => patch('theme', e.target.value)} className={inputCls}>
            <option value="green">Deep green (default)</option>
            <option value="cream">Cream</option>
            <option value="gold">Gold</option>
          </select>
        </Field>
        <Field label="Speed">
          <select value={c.speed || 'medium'} onChange={(e) => patch('speed', e.target.value)} className={inputCls}>
            <option value="slow">Slow</option>
            <option value="medium">Medium</option>
            <option value="fast">Fast</option>
          </select>
        </Field>
        <Field label="Direction">
          <select value={c.direction || 'left'} onChange={(e) => patch('direction', e.target.value)} className={inputCls}>
            <option value="left">Left ←</option>
            <option value="right">Right →</option>
          </select>
        </Field>
        <Field label="Separator">
          <select value={c.separator || 'diamond'} onChange={(e) => patch('separator', e.target.value)} className={inputCls}>
            <option value="diamond">◆ Diamond</option>
            <option value="dot">• Dot</option>
            <option value="slash">/ Slash</option>
            <option value="none">None</option>
          </select>
        </Field>
      </div>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-2">Items</div>
        <RepeaterList
          items={c.items || []}
          onChange={(v) => patch('items', v)}
          addLabel="Add marquee item"
          newItem={() => ({ text: 'NEW ANNOUNCEMENT', icon: 'Sparkles', image_url: '' })}
          max={20}
          renderItem={(it, i, up) => (
            <div className="space-y-3">
              <Field label="Text"><TextInput value={it.text || ''} onChange={(e) => up('text', e.target.value)} placeholder="e.g. WORLD-CLASS PADEL COURTS" /></Field>
              <div className="grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 items-start">
                <Field label="Icon (lucide name)" hint="Leave blank to show only text or an image."><TextInput value={it.icon || ''} onChange={(e) => up('icon', e.target.value)} placeholder="Sparkles" /></Field>
                <ImagePicker
                  label="Image (optional)"
                  section="marquee"
                  aspect="1/1"
                  value={it.image_url}
                  onChange={(url) => up('image_url', url)}
                />
              </div>
            </div>
          )}
        />
      </div>
    </>
  );
}

function AboutEditor({ content, onChange }) {
  const c = content || {};
  const patch = (field, value) => onChange({ ...c, [field]: value });
  return (
    <>
      <SectionTitle hint="Story, stats and framed inset image.">About the Project</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Eyebrow"><TextInput value={c.eyebrow || ''} onChange={(e) => patch('eyebrow', e.target.value)} /></Field>
        <Field label="Heading top"><TextInput value={c.heading_top || ''} onChange={(e) => patch('heading_top', e.target.value)} /></Field>
        <Field label="Heading (cursive)"><TextInput value={c.heading_script || ''} onChange={(e) => patch('heading_script', e.target.value)} /></Field>
      </div>
      <Field label="Body copy" className="mt-4">
        <TextArea rows={4} value={c.body || ''} onChange={(e) => patch('body', e.target.value)} />
      </Field>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <ImagePicker label="Background image" section="about" aspect="16/9" value={c.bg_image_url} onChange={(url) => patch('bg_image_url', url)} />
        <ImagePicker label="Inset (framed) image" section="about" aspect="4/3" value={c.inset_image_url} onChange={(url) => patch('inset_image_url', url)} />
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Field label="Inset caption"><TextInput value={c.inset_label || ''} onChange={(e) => patch('inset_label', e.target.value)} /></Field>
        <Field label="Inset sub-caption"><TextInput value={c.inset_sub || ''} onChange={(e) => patch('inset_sub', e.target.value)} /></Field>
      </div>
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-2">Stats (3 columns)</div>
        <RepeaterList
          items={c.stats || []}
          onChange={(v) => patch('stats', v)}
          addLabel="Add stat"
          newItem={() => ({ icon: 'Star', label: 'NEW', sub: 'Detail' })}
          max={4}
          renderItem={(it, i, up) => (
            <div className="grid grid-cols-3 gap-3">
              <Field label="Icon (lucide)"><TextInput value={it.icon || ''} onChange={(e) => up('icon', e.target.value)} placeholder="Mountain" /></Field>
              <Field label="Label"><TextInput value={it.label || ''} onChange={(e) => up('label', e.target.value)} /></Field>
              <Field label="Sub"><TextInput value={it.sub || ''} onChange={(e) => up('sub', e.target.value)} /></Field>
            </div>
          )}
        />
      </div>
    </>
  );
}

function ServicesEditor({ content, onChange }) {
  const c = content || {};
  const patch = (field, value) => onChange({ ...c, [field]: value });
  return (
    <>
      <SectionTitle hint="Show off your offerings with 2 to 6 cards.">Services</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Eyebrow"><TextInput value={c.eyebrow || ''} onChange={(e) => patch('eyebrow', e.target.value)} /></Field>
        <Field label="Heading top"><TextInput value={c.heading_top || ''} onChange={(e) => patch('heading_top', e.target.value)} /></Field>
        <Field label="Heading (cursive)"><TextInput value={c.heading_script || ''} onChange={(e) => patch('heading_script', e.target.value)} /></Field>
      </div>
      <Field label="Subhead (right of headline)" className="mt-4">
        <TextArea rows={2} value={c.subhead || ''} onChange={(e) => patch('subhead', e.target.value)} />
      </Field>
      <div className="mt-4 max-w-sm">
        <ImagePicker label="Section background (subtle)" section="services" aspect="16/9" value={c.bg_image_url} onChange={(url) => patch('bg_image_url', url)} />
      </div>
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-2">Service cards</div>
        <RepeaterList
          items={c.cards || []}
          onChange={(v) => patch('cards', v)}
          addLabel="Add service card"
          newItem={() => ({ title: 'NEW SERVICE', body: 'Short description of the offering.', image_url: '' })}
          max={6}
          renderItem={(it, i, up) => (
            <div className="grid md:grid-cols-[1fr_220px] gap-4">
              <div className="space-y-3">
                <Field label="Title"><TextInput value={it.title || ''} onChange={(e) => up('title', e.target.value)} /></Field>
                <Field label="Description"><TextArea rows={3} value={it.body || ''} onChange={(e) => up('body', e.target.value)} /></Field>
              </div>
              <ImagePicker label="Card image" section="services" aspect="3/4" value={it.image_url} onChange={(url) => up('image_url', url)} />
            </div>
          )}
        />
      </div>
    </>
  );
}

function CommunityEditor({ content, onChange }) {
  const c = content || {};
  const patch = (field, value) => onChange({ ...c, [field]: value });
  return (
    <>
      <SectionTitle hint="Immersive banner with quote-style headline and pillars.">Community banner</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Heading line 1"><TextInput value={c.heading_line1 || ''} onChange={(e) => patch('heading_line1', e.target.value)} /></Field>
        <Field label="Heading line 2"><TextInput value={c.heading_line2 || ''} onChange={(e) => patch('heading_line2', e.target.value)} /></Field>
        <Field label="Heading line 3"><TextInput value={c.heading_line3 || ''} onChange={(e) => patch('heading_line3', e.target.value)} /></Field>
        <Field label="Cursive word"><TextInput value={c.heading_script || ''} onChange={(e) => patch('heading_script', e.target.value)} /></Field>
      </div>
      <Field label="Body copy" className="mt-4">
        <TextArea rows={3} value={c.body || ''} onChange={(e) => patch('body', e.target.value)} />
      </Field>
      <div className="mt-4 max-w-sm">
        <ImagePicker label="Background image" section="community" aspect="16/9" value={c.bg_image_url} onChange={(url) => patch('bg_image_url', url)} />
      </div>
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-2">Pillars (3 recommended)</div>
        <RepeaterList
          items={c.pillars || []}
          onChange={(v) => patch('pillars', v)}
          addLabel="Add pillar"
          newItem={() => ({ icon: 'Sparkles', title: 'NEW', sub: 'PILLAR' })}
          max={4}
          renderItem={(it, i, up) => (
            <div className="grid grid-cols-3 gap-3">
              <Field label="Icon (lucide)"><TextInput value={it.icon || ''} onChange={(e) => up('icon', e.target.value)} placeholder="Leaf" /></Field>
              <Field label="Title"><TextInput value={it.title || ''} onChange={(e) => up('title', e.target.value)} /></Field>
              <Field label="Sub"><TextInput value={it.sub || ''} onChange={(e) => up('sub', e.target.value)} /></Field>
            </div>
          )}
        />
      </div>
    </>
  );
}

function FooterEditor({ content, onChange }) {
  const c = content || {};
  const patch = (field, value) => onChange({ ...c, [field]: value });
  return (
    <>
      <SectionTitle hint="Location, social handles, CTA and copyright.">Footer</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Location line 1"><TextInput value={c.location_line1 || ''} onChange={(e) => patch('location_line1', e.target.value)} /></Field>
        <Field label="Location line 2"><TextInput value={c.location_line2 || ''} onChange={(e) => patch('location_line2', e.target.value)} /></Field>
        <Field label="CTA label"><TextInput value={c.cta_label || ''} onChange={(e) => patch('cta_label', e.target.value)} /></Field>
        <Field label="Copyright line"><TextInput value={c.copyright || ''} onChange={(e) => patch('copyright', e.target.value)} /></Field>
      </div>
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-2">Social handles</div>
        <RepeaterList
          items={c.socials || []}
          onChange={(v) => patch('socials', v)}
          addLabel="Add social"
          newItem={() => ({ icon: 'Instagram', label: '@handle', url: 'https://' })}
          max={6}
          renderItem={(it, i, up) => (
            <div className="grid grid-cols-3 gap-3">
              <Field label="Icon (lucide)"><TextInput value={it.icon || ''} onChange={(e) => up('icon', e.target.value)} placeholder="Instagram" /></Field>
              <Field label="Label"><TextInput value={it.label || ''} onChange={(e) => up('label', e.target.value)} /></Field>
              <Field label="URL"><TextInput value={it.url || ''} onChange={(e) => up('url', e.target.value)} placeholder="https://" /></Field>
            </div>
          )}
        />
      </div>
    </>
  );
}

function NavbarEditor({ content, onChange }) {
  const c = content || {};
  const patch = (field, value) => onChange({ ...c, [field]: value });
  return (
    <>
      <SectionTitle hint="Menu labels + the location pill in the top-right.">Navigation</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Location pill line 1"><TextInput value={c.location_pill_line1 || ''} onChange={(e) => patch('location_pill_line1', e.target.value)} /></Field>
        <Field label="Location pill line 2"><TextInput value={c.location_pill_line2 || ''} onChange={(e) => patch('location_pill_line2', e.target.value)} /></Field>
      </div>
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-2">Menu items</div>
        <RepeaterList
          items={c.menu || []}
          onChange={(v) => patch('menu', v)}
          addLabel="Add menu item"
          newItem={() => ({ label: 'NEW LINK', target: 'home' })}
          max={8}
          renderItem={(it, i, up) => (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Label"><TextInput value={it.label || ''} onChange={(e) => up('label', e.target.value)} /></Field>
              <Field label="Target section id" hint="e.g. home, about, services, community, contact"><TextInput value={it.target || ''} onChange={(e) => up('target', e.target.value)} /></Field>
            </div>
          )}
        />
      </div>
    </>
  );
}
